import React, { FC } from 'react';
import './PermissionTableForm.css';
import {
    CheckBoxField,
    FormikErrors,
    AccessRightKey,
    ActionButton
} from '@/Shared';
import { PermissionFormValues, PermissionFormViewModel } from '../../Logic';

interface PermissionTableFormProps {
    entityFormVM: PermissionFormViewModel;
    values: PermissionFormValues;
    setFieldValue: (
        field: string,
        value: any,
        shouldValidate?: boolean | undefined
    ) => Promise<void | FormikErrors<PermissionFormValues>>

}

/**
 * =====================================================
 * Component: PermissionTableForm
 * =====================================================
 *
 * Description:
 * Renders a permission table for a specific role with resources and access rights.
 * Provides:
 * - "Owner" checkbox per resource and global "Mes données"
 * - Per-resource "all" checkbox for all actions
 * - Global "Tout" checkbox for all actions across all resources
 * - Individual action checkboxes per resource and per column
 *
 * Integrates with Formik for state management.
 *
 * Author: Samano CASTRE
 * Date: July 17, 2025
 */
const PermissionTableForm: FC<PermissionTableFormProps> = ({ entityFormVM, values, setFieldValue }) => {

    // Toggles the "allRecords" field for all resources (global "Mes données")
    const handleGlobalOwnerChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const checked = e.target.checked;
        Object.keys(values.resourceAccessList).forEach(domain => {
            setFieldValue(`resourceAccessList.${domain}.allRecords`, checked);
        });
    }

    // Toggles all actions for all resources (global "Tout")
    const handleGlobalAllChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const checked = e.target.checked;
        console.log(checked);
        Object.keys(values.resourceAccessList).forEach(
            resource => {
                entityFormVM.accessOptions.forEach(
                    accessOpt => setFieldValue(`resourceAccessList.${resource}.${accessOpt.value}`, checked)
                );
            }
        );
    }

    // Toggles a specific action across all resources (per-column header)
    const handleGlobalAccessChange = (e: React.ChangeEvent<HTMLInputElement>, right: string) => {
        const checked = e.target.checked;
        Object.keys(values.resourceAccessList).forEach(domain => {
            setFieldValue(`resourceAccessList.${domain}.${right}`, checked);
        });
    }

    // Toggles all actions for a single resource (per-row "all" checkbox)
    const handleResourceAllChange = (e: React.ChangeEvent<HTMLInputElement>, resource: string) => {
        const checked = e.target.checked;
        //setFieldValue(`resourceAccessList.${resource}.all`, checked)

        entityFormVM.accessOptions.forEach(opt =>
            setFieldValue(`resourceAccessList.${resource}.${opt.value}`, checked)
        );
    }

    return (<div className="PermissionTableForm DataTable" data-testid="DataTable">
        <div className="DataTable__body sm-scroll">
            <h3 className='DataTable__title'>Permission des {entityFormVM.getRoleLabel(values.role)}s</h3>
            <table className='data-table'>
                <thead className='sticky'>
                    <tr>
                        <th>Resource</th>
                        <th>
                            <input
                                type="checkbox"
                                checked={
                                    Object.values(values.resourceAccessList).every(resource => resource['allRecords'])
                                }
                                onChange={handleGlobalOwnerChange}
                            /> Toutes données
                        </th>
                        <th>
                            <input
                                type="checkbox"

                                checked={
                                    Object.values(values.resourceAccessList)
                                        .every(resource => entityFormVM.accessOptions
                                            .every(accessOpt => resource[accessOpt.value as AccessRightKey]))
                                }
                                onChange={handleGlobalAllChange}
                            /> Tout</th>
                        {
                            entityFormVM.accessOptions.map(
                                opt => (
                                    <th key={opt.key}>
                                        <input
                                            name={`resourceAccessList.${opt.value}`}
                                            type="checkbox"
                                            checked={Object.values(values.resourceAccessList).every(res => res[opt.value as AccessRightKey])}
                                            onChange={(e) => handleGlobalAccessChange(e, opt.value as string)}
                                        />  {opt.label}
                                    </th>
                                )
                            )
                        }
                    </tr>
                </thead>
                <tbody>
                    {
                        entityFormVM.resourceOptions.map(
                            resOption => <tr key={resOption.key}>
                                <td>{resOption.label}</td>
                                <td>
                                    <CheckBoxField
                                        name={`resourceAccessList.${resOption.value}.allRecords`}
                                    />
                                </td>
                                <td>
                                    <input
                                        name={`resourceAccessList.${resOption.value}.all`}
                                        type="checkbox"
                                        checked={entityFormVM.accessOptions.every(
                                            accessOpt => values.resourceAccessList?.[resOption.value]?.[accessOpt.value as AccessRightKey] === true
                                        )}
                                        onChange={(e) => handleResourceAllChange(e, resOption.value as string)}
                                    />
                                </td>


                                {
                                    entityFormVM.accessOptions.map(
                                        accessOpt => (
                                            <td key={accessOpt.key}>
                                                <CheckBoxField
                                                    name={`resourceAccessList.${resOption.value}.${accessOpt.value}`}
                                                />
                                            </td>
                                        )
                                    )
                                }
                            </tr>
                        )
                    }
                </tbody>
            </table>
        </div>
    </div>)

}

export default PermissionTableForm;